import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  IconDownloadAlt,
  IconSearch,
  TooltipTrigger,
  Tooltip,
} from '@spotify-internal/encore-creator-web';
import moment from 'moment';
import styles from './index.module.scss';
import { selectDateRange, selectDatesAvailable, selectFeatures } from 'store/selectors';
import { updateDateRange } from 'store/updateDateRange';
import { trackEvent } from 'lib/Tracking';
import { Dropdown } from 'vendor/Dropdown';
import { Calendar } from 'vendor/Calendar';
import { FormattedMessage, injectIntl, defineMessage } from 'react-intl';

export class InternationalizedFilterWithDatePicker extends PureComponent {
  static propTypes = {
    dateRange: PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
    updateDateRange: PropTypes.func.isRequired,
    datesAvailable: PropTypes.shape({
      from: PropTypes.string.isRequired,
      until: PropTypes.string.isRequired,
    }),
    downloadDataAsCsv: PropTypes.func.isRequired,
    onUserInput: PropTypes.func.isRequired,
    value: PropTypes.string,
    csvDownloadTrackingLabel: PropTypes.string.isRequired,
    showFirstDaysOptions: PropTypes.bool,
  };

  state = {
    isExpanded: false,
    isCustomDate: false,
    showCsvTooltip: false,
  };

  componentDidMount() {
    window.addEventListener('mousedown', this.onWindowEvent, true);
  }

  componentWillUnmount() {
    /* istanbul ignore next */
    window.removeEventListener('mousedown', this.onWindowEvent, true);
  }

  onDatesChange = ({ startDate, endDate, label }) => {
    this.props.updateDateRange(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), label);
    trackEvent({ category: 'date', action: 'change', label });
    this.clearState();
  };

  onWindowEvent = ({ type, target }) => {
    /* istanbul ignore else */
    if (type === 'mousedown' && !this.element.contains(target)) {
      this.clearState();
    }
  };

  onIsCustomDate = () => this.setState({ isCustomDate: true });

  get expandedElement() {
    const { startDate, endDate } = this.props.dateRange;
    return this.state.isCustomDate ? (
      <Calendar
        onSave={this.onDatesChange}
        onCancel={this.clearState}
        datesAvailable={this.props.datesAvailable}
        startDate={moment(startDate)}
        endDate={moment(endDate)}
      />
    ) : (
      <Dropdown
        onDatesChange={this.onDatesChange}
        onIsCustomDate={this.onIsCustomDate}
        endDate={this.props.datesAvailable.until}
        showFirstDaysOptions={this.props.showFirstDaysOptions}
      />
    );
  }

  get downloadButton() {
    /* istanbul ignore next */ return (
      <div className={styles.csvDownload}>
        <TooltipTrigger
          overlay={
            this.state.showCsvTooltip && (
              <Tooltip>
                <FormattedMessage id="filter-date-picker.tooltip" defaultMessage="Download CSV" />
              </Tooltip>
            )
          }
          placement={TooltipTrigger.top}
          onShow={() => this.setState({ showCsvTooltip: true })}
          onHide={() => this.setState({ showCsvTooltip: false })}
        >
          <IconDownloadAlt iconSize={32} onClick={this.downloadCSV} data-testid="download" />
        </TooltipTrigger>
      </div>
    );
  }

  get datePicker() {
    return (
      <div
        data-qa="date-range-selector"
        ref={el => {
          this.element = el;
        }}
        className={styles.datePicker}
      >
        <button className={styles.button} onClick={this.toggleIsExpanded}>
          {this.props.dateRange.label}
          <svg className={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path d="M8 13.77L.62 5.129l.76-.649L8 12.231l6.62-7.751.76.649z" />
          </svg>
        </button>
        {this.state.isExpanded && this.expandedElement}
      </div>
    );
  }

  get filter() {
    const { intl } = this.props;
    const placeholderMsg = defineMessage({
      id: 'filter-date-picker.input.placeholder',
      defaultMessage: 'Filter',
    });
    const placeholder = intl.formatMessage(placeholderMsg);
    return (
      <div data-qa="filter" className={styles.filter}>
        <label className={styles.filter_label} htmlFor="filter-input">
          <IconSearch iconSize={24} className={styles.search} />
        </label>
        <input
          className={styles.filter_input}
          id="filter-input"
          type="text"
          placeholder={placeholder}
          data-qa="filter-input"
          onChange={this.handleChange}
          value={this.props.value}
        />
      </div>
    );
  }

  toggleIsExpanded = () => {
    const { isExpanded } = this.state;
    this.clearState();
    this.setState({ isExpanded: !isExpanded });
  };

  downloadCSV = () => {
    trackEvent({ category: 'csv', action: 'download', label: this.props.csvDownloadTrackingLabel });
    this.props.downloadDataAsCsv();
  };

  clearState = () => {
    this.setState({
      isCustomDate: false,
      isExpanded: false,
    });
  };

  handleChange = ({ target: { value } }) => this.props.onUserInput(value);

  render() {
    return (
      <div
        data-qa="filter-with-date-picker"
        className={styles.container}
        id="filter-with-date-picker"
      >
        {this.filter}
        {this.datePicker}
        {this.downloadButton}
      </div>
    );
  }
}

const selectors = createStructuredSelector({
  dateRange: selectDateRange,
  datesAvailable: selectDatesAvailable,
  features: selectFeatures,
});
const actions = { updateDateRange };

const FilterWithDatePicker = injectIntl(InternationalizedFilterWithDatePicker);
export const ConnectedFilterWithDatePicker = connect(selectors, actions)(FilterWithDatePicker);
